import React, { useState, useMemo, useRef, useCallback, useEffect, memo } from 'react';
import { StyleSheet, View, Pressable, Platform, Dimensions, LayoutChangeEvent, useWindowDimensions, Keyboard } from 'react-native';
import { ScrollView } from 'react-native';
import GenerateArray from './Components/GenerateArray';
import { SearchComponentTypes } from './Types';
import InteractiveSearch from './Components/InteractiveSearch';
import { useNavigation } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import LoadingIndicator from '../Spinner';
import { useDispatch } from 'react-redux';
import { useDeviceType } from '@hooks/useDeviceType';
import { AICreditsModal } from '@components/modals/SaaSModals';
import { useBillingInfoWithContext, useCompanyNumber } from '@utils/billingUtils';
import { useQueryClient } from '@tanstack/react-query';
import { getModernActionStyle } from '@stylesheet';
import styles from '@stylesheet';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  interpolate,
  Extrapolation,
  Easing,
  runOnJS,
} from 'react-native-reanimated';

// Enhanced responsive breakpoints
const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

const SearchComponent = ({
  removeFlex = false,
  aiCallback,
  placeHolder,
  aiSearch = false,
  searchState,
  setSearchState,
  searchOnly = false,
  minWidth,
  backButtonCallback,
  onlyOthers = false,
  customIcons,
  disableFilter,
  otherComponents,
  customIconStyle,
  backButton,
  backButtonStyle,
  showSearch = true,
  actionButton,
  action,
  searchParams,
  customStyle,
  callback,
  helpComponent,
  fetchingState,
  extraComponents,
  inverse,
  disableBurger,
  fullWidthOnTablet = false
}: SearchComponentTypes) => {
  const [extendedMenu, setExtendedMenu] = useState(false);
  const [showExtendedMenu, setShowExtendedMenu] = useState(extendedMenu);
  const [containerLayout, setContainerLayout] = useState({ width: 0, height: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showAICreditsModal, setShowAICreditsModal] = useState(false);

  // Billing info for AI credits check
  const companyNumber = useCompanyNumber();
  const billingInfo = useBillingInfoWithContext();



  const buttonRef = useRef<any>(null);
  const containerRef = useRef<View>(null);
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const queryClient = useQueryClient();

  // Use useWindowDimensions hook for better performance
  const screenDimensions = useWindowDimensions();

  // Animation values for expansion (only when aiCallback exists)
  const shouldExpand = !!aiCallback;
  const expandedHeight = useSharedValue(0);
  const backdropOpacity = useSharedValue(0);
  const containerScale = useSharedValue(1);
  const containerTop = useSharedValue(0);
  const baseContainerHeight = useSharedValue(0);

  // Initialize search state
  useEffect(() => {
    if (setSearchState && !searchState) {
      setSearchState('');
    }
  }, [setSearchState, searchState]);

  // Initialize containerTop to base position when aiCallback is present
  // Since position is handled at screen level, use the original top position
  useEffect(() => {
    if (shouldExpand) {
      const baseTop = Platform.OS === 'ios' ? 15 : 20;
      containerTop.value = baseTop;
    }
  }, [shouldExpand, containerTop]);

  // Memoized search callback
  const handleSearchCallback = useCallback((text: string) => {
    if (setSearchState) {
      setSearchState(text || '');
    }
    if (callback) {
      callback(text);
    }
  }, [setSearchState, callback]);

  // Check if user has enough AI credits before allowing generation
  const checkAICredits = useCallback(() => {
    // If billing info is still loading, allow the call (will be checked on backend)
    if (billingInfo?.aiUsage?.isLoading) {
      console.log('🔄 [AI Credits] Billing info loading, allowing call (backend will validate)');
      return true;
    }

    // If user has active subscription, they have unlimited credits
    if (billingInfo?.hasActiveSubscription) {
      console.log('✅ [AI Credits] Active subscription - unlimited credits');
      return true;
    }

    // For free plan users, check usage stats
    const currentUsage = billingInfo?.aiUsage?.current || 0;
    const limit = billingInfo?.aiUsage?.limit || 5;
    const remainingCredits = limit - currentUsage;

    console.log('🔍 [AI Credits] Check:', {
      currentUsage,
      limit,
      remainingCredits,
      hasCredits: remainingCredits > 0,
      billingInfo: billingInfo?.aiUsage
    });

    // Check if user has credits remaining (remainingCredits > 0)
    return remainingCredits > 0;
  }, [billingInfo]);

  // Wrapped AI callback that checks credits before executing
  const wrappedAICallback = useCallback(async (text: string) => {
    // Check if user has enough credits
    if (!checkAICredits()) {
      console.log('❌ [AI Credits] Insufficient credits, showing modal');
      // Show credits modal
      setShowAICreditsModal(true);
      return;
    }

    // If credits are available, proceed with AI callback
    if (aiCallback) {
      try {
        console.log('✅ [AI Credits] Credits available, proceeding with AI generation');
        await aiCallback(text);

        // Refetch billing info after successful AI call to update credit count
        // Use a small delay to allow backend to update the count
        setTimeout(() => {
          console.log('🔄 [AI Credits] Refetching billing info after AI call');
          // Invalidate all related queries to ensure fresh data
          queryClient.invalidateQueries({ queryKey: ['aiUsageStats'] });
          queryClient.invalidateQueries({ queryKey: ['subscriptionPlan'] });
          if (companyNumber) {
            queryClient.invalidateQueries({ queryKey: ['subscriptionPlan', companyNumber] });
          }
          // Force refetch immediately
          queryClient.refetchQueries({ queryKey: ['aiUsageStats'] });
        }, 1500);
      } catch (error) {
        console.error('❌ [AI Credits] AI callback error:', error);
        // Even on error, refetch billing info in case it was partially processed
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['aiUsageStats'] });
        }, 1000);
        throw error;
      }
    }
  }, [aiCallback, checkAICredits, queryClient]);

  // Animation configuration
  const ANIMATION_CONFIG = useMemo(() => ({
    duration: 300,
    easing: Easing.out(Easing.cubic),
  }), []);

  const SPRING_CONFIG = useMemo(() => ({
    damping: 15,
    stiffness: 150,
    mass: 1,
    overshootClamping: false,
  }), []);

  // Expanded height calculation
  const expandedHeightValue = useMemo(() => {
    if (!shouldExpand) return 0;
    return screenDimensions.height * 0.4; // 40% of screen height
  }, [shouldExpand, screenDimensions.height]);

  // Handle input focus - expand when aiCallback exists
  const handleInputFocus = useCallback(() => {
    if (!shouldExpand) return;

    setIsFocused(true);
    setIsExpanded(true);

    // Animate expansion - only expand height, keep top position unchanged
    expandedHeight.value = withTiming(expandedHeightValue, ANIMATION_CONFIG);
    backdropOpacity.value = withTiming(1, ANIMATION_CONFIG);
    // Don't use scale transform - it causes shifting
    // containerScale.value = withSpring(1.02, SPRING_CONFIG);
    // Keep top position at base - don't move down
  }, [shouldExpand, expandedHeightValue, expandedHeight, backdropOpacity, ANIMATION_CONFIG]);

  // Handle input blur
  const handleInputBlur = useCallback(() => {
    if (!shouldExpand) return;
    // Don't blur if expanded (user might be interacting with other elements)
    if (!isExpanded) {
      setIsFocused(false);
    }
  }, [shouldExpand, isExpanded]);

  // Handle collapse
  const handleCollapse = useCallback(() => {
    if (!shouldExpand) return;

    // Animate collapse first - this ensures smooth animation
    expandedHeight.value = withTiming(0, {
      duration: 250,
    }, (finished) => {
      if (finished) {
        // Ensure height is fully reset (this runs on UI thread, but setting shared value is safe)
        expandedHeight.value = 0;
      }
    });
    backdropOpacity.value = withTiming(0, { duration: 250 });

    // Update state immediately after starting animation
    // This ensures containerStyles recalculates with correct base height
    // The animated style will stop applying since isExpanded becomes false
    setIsFocused(false);
    setIsExpanded(false);

    // Dismiss keyboard
    Keyboard.dismiss();
  }, [shouldExpand, expandedHeight, backdropOpacity]);

  // Handle backdrop press
  const handleBackdropPress = useCallback(() => {
    handleCollapse();
  }, [handleCollapse]);

  // Animated styles
  const containerAnimatedStyle = useAnimatedStyle(() => {
    // When collapsed (height is 0 or very small), use base container height
    // This ensures consistent height during collapse animation and prevents position shifts
    if (expandedHeight.value === 0 || expandedHeight.value < 10) {
      return {
        height: baseContainerHeight.value,
      } as any;
    }

    // When expanded, set the height to the expanded value
    // Since container is absolutely positioned, this expansion overlays content
    // and doesn't push it down in the layout flow
    return {
      height: expandedHeight.value,
      // Ensure overflow is visible so expanded content can show
      overflow: 'visible' as const,
    } as any;
  }, []);

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  // Enhanced responsive utilities
  const currentBreakpoint = useMemo(() => {
    const width = screenDimensions.width;
    if (width >= BREAKPOINTS.xxl) return 'xxl';
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  }, [screenDimensions.width]);

  // Responsive configuration based on breakpoint
  const responsiveConfig = useMemo(() => {
    const configs = {
      xs: {
        containerHeight: Platform.OS === 'android' ? 52 : 44,
        searchBorderRadius: 6,
        iconSize: 18,
        buttonSize: 32,
        spacing: { horizontal: 6, vertical: 4 },
        padding: { horizontal: 28, vertical: 4 },
        maxContainerWidth: screenDimensions.width - 60,
        searchMinWidth: 200,
        showAllComponents: false,
        maxVisibleComponents: 2,
      },
      sm: {
        containerHeight: Platform.OS === 'android' ? 56 : 48,
        searchBorderRadius: 8,
        iconSize: 20,
        buttonSize: 36,
        spacing: { horizontal: 8, vertical: 6 },
        padding: { horizontal: 12, vertical: 6 },
        maxContainerWidth: screenDimensions.width - 32,
        searchMinWidth: 250,
        showAllComponents: false,
        maxVisibleComponents: 3,
      },
      md: {
        containerHeight: Platform.OS === 'android' ? 60 : 52,
        searchBorderRadius: 10,
        iconSize: 22,
        buttonSize: 40,
        spacing: { horizontal: 12, vertical: 8 },
        padding: { horizontal: 16, vertical: 8 },
        maxContainerWidth: Math.min(screenDimensions.width - 64, 900),
        searchMinWidth: 300,
        showAllComponents: true,
        maxVisibleComponents: 4,
      },
      lg: {
        containerHeight: Platform.OS === 'android' ? 64 : 56,
        searchBorderRadius: 12,
        iconSize: 24,
        buttonSize: 44,
        spacing: { horizontal: 16, vertical: 10 },
        padding: { horizontal: 20, vertical: 10 },
        maxContainerWidth: Math.min(screenDimensions.width - 80, 1100),
        searchMinWidth: 400,
        showAllComponents: true,
        maxVisibleComponents: 5,
      },
      xl: {
        containerHeight: Platform.OS === 'android' ? 68 : 60,
        searchBorderRadius: 14,
        iconSize: 26,
        buttonSize: 48,
        spacing: { horizontal: 20, vertical: 12 },
        padding: { horizontal: 24, vertical: 12 },
        maxContainerWidth: Math.min(screenDimensions.width - 96, 1200),
        searchMinWidth: 450,
        showAllComponents: true,
        maxVisibleComponents: 6,
      },
      xxl: {
        containerHeight: Platform.OS === 'android' ? 72 : 64,
        searchBorderRadius: 16,
        iconSize: 28,
        buttonSize: 52,
        spacing: { horizontal: 24, vertical: 14 },
        padding: { horizontal: 28, vertical: 14 },
        maxContainerWidth: Math.min(screenDimensions.width - 112, 1400),
        searchMinWidth: 500,
        showAllComponents: true,
        maxVisibleComponents: 8,
      },
    };

    const config = configs[currentBreakpoint];

    // Store base container height in shared value for use in animated style
    baseContainerHeight.value = config.containerHeight;

    return config;
  }, [currentBreakpoint, screenDimensions.width, baseContainerHeight]);

  // Calculate base container width for burger menu calculation
  const baseContainerWidth = useMemo(() => {
    // For tablet full-width mode, use screen width minus padding
    // Otherwise use the responsive config maxContainerWidth
    if (isTablet && fullWidthOnTablet) {
      const containerPadding = 20;
      return screenDimensions.width - (containerPadding * 2);
    }
    return responsiveConfig.maxContainerWidth;
  }, [isTablet, fullWidthOnTablet, screenDimensions.width, responsiveConfig.maxContainerWidth]);

  // Calculate if burger menu should be shown
  const showBurgerMenu = useMemo(() => {
    if (!customIcons || customIcons?.length === 0 || disableBurger) return false;
    // Allow burger menu on tablet when fullWidthOnTablet is true, but not on web
    if (Platform.OS === 'web') return false;
    if (isTablet && !fullWidthOnTablet) return false;

    // On tablet with fullWidthOnTablet, be more aggressive - show burger menu if 3+ icons
    if (isTablet && fullWidthOnTablet && customIcons?.length >= 3) {
      return true;
    }

    // Calculate actual space requirements similar to componentWidths calculation
    const containerPadding = isTablet && fullWidthOnTablet ? 20 : responsiveConfig.padding.horizontal;
    const maxContainerWidth = baseContainerWidth;

    // Calculate what the icon container width would be if we showed all icons
    const iconSize = responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal;
    const uncappedIconWidth = (customIcons?.length || 0) * iconSize;
    const iconContainerWidthCap = aiSearch ? 100 : 200;
    const iconContainerWidth = Math.min(uncappedIconWidth, iconContainerWidthCap);

    // Calculate other reserved space (padding, other components, etc.)
    let totalReservedWidth = containerPadding * 2; // Container padding

    // Add other components width if they exist
    const hasOtherComponents = otherComponents && otherComponents.length > 0;
    if (hasOtherComponents) {
      const maxOtherComponents = responsiveConfig.showAllComponents ?
        (otherComponents?.length || 0) : responsiveConfig.maxVisibleComponents;
      const otherComponentsWidth = Math.min(
        maxOtherComponents * (responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal),
        aiSearch ? 150 : 250
      );
      totalReservedWidth += otherComponentsWidth;
    }

    // Add icon container width
    totalReservedWidth += iconContainerWidth;

    // Calculate available width for search
    const availableWidth = maxContainerWidth - totalReservedWidth;

    // Show burger menu if:
    // 1. Uncapped icon width exceeds the cap (would be truncated), OR
    // 2. Icons would take more than 25% of container width, OR
    // 3. Available width for search would be less than minimum search width
    const iconsPercentage = (iconContainerWidth / maxContainerWidth) * 100;
    const wouldExceedSearchMin = availableWidth < responsiveConfig.searchMinWidth;

    return uncappedIconWidth > iconContainerWidthCap || iconsPercentage > 25 || wouldExceedSearchMin;
  }, [
    customIcons,
    disableBurger,
    isTablet,
    fullWidthOnTablet,
    baseContainerWidth,
    responsiveConfig.buttonSize,
    responsiveConfig.spacing.horizontal,
    aiSearch,
    otherComponents,
    responsiveConfig.showAllComponents,
    responsiveConfig.maxVisibleComponents,
    responsiveConfig.searchMinWidth
  ]);

  // Calculate component widths based on available space
  const componentWidths = useMemo(() => {
    const hasCustomIcons = customIcons && customIcons.length > 0;
    const hasOtherComponents = otherComponents && otherComponents.length > 0;
    const hasActionButton = !!actionButton;
    const hasExtraComponents = !!extraComponents;

    // Use the same base container width calculation as burger menu
    // For tablet full-width mode, use screen width minus padding
    // Otherwise use the responsive config maxContainerWidth
    const containerPadding = isTablet && fullWidthOnTablet ? 20 : responsiveConfig.padding.horizontal;
    const maxContainerWidth = baseContainerWidth;

    let totalReservedWidth = containerPadding * 2; // Container padding

    // Calculate icon container width - account for actual icon size (48px) + gap (12px) = ~60px per icon
    // Each icon in GenerateArray is 48px wide with 12px gap, so total per icon is ~60px
    const iconSize = 48; // Actual icon button width from GenerateArray
    const iconGap = 12; // Gap between icons from GenerateArray
    const iconCount = customIcons?.length || 0;
    const calculatedIconWidth = iconCount > 0 ? iconCount * (iconSize + iconGap) + iconGap : 0; // Add extra gap for padding
    // Cap the width but ensure it's large enough for all icons
    const iconContainerWidth = hasCustomIcons ?
      Math.max(calculatedIconWidth, Math.min(calculatedIconWidth, aiSearch ? 180 : 250)) : 0;

    // Calculate other components width (hide when burger is shown)
    const maxOtherComponents = responsiveConfig.showAllComponents ?
      (otherComponents?.length || 0) : responsiveConfig.maxVisibleComponents;
    const otherComponentsWidth = (hasOtherComponents && !showBurgerMenu) ?
      Math.min(maxOtherComponents * (responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal), aiSearch ? 150 : 250) : 0;

    // Calculate action button width
    const actionButtonWidth = hasActionButton ? responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal : 0;

    // Calculate extra components width (hide when burger is shown)
    const extraComponentsWidth = (hasExtraComponents && !showBurgerMenu) ? responsiveConfig.buttonSize + responsiveConfig.spacing.horizontal : 0;

    totalReservedWidth += iconContainerWidth + otherComponentsWidth + actionButtonWidth + extraComponentsWidth;

    // Calculate available width in parent container
    const availableWidth = maxContainerWidth - totalReservedWidth;

    // When aiSearch is present, search takes 80-85% of available width in parent
    // Otherwise, use all available width
    const searchWidth = aiSearch
      ? Math.max(responsiveConfig.searchMinWidth, availableWidth * 0.82) // 82% of available width
      : Math.max(responsiveConfig.searchMinWidth, availableWidth);

    return {
      container: maxContainerWidth,
      search: searchWidth,
      icons: iconContainerWidth,
      otherComponents: otherComponentsWidth,
      actionButton: actionButtonWidth,
      extraComponents: extraComponentsWidth,
    };
  }, [
    responsiveConfig,
    customIcons,
    otherComponents,
    actionButton,
    extraComponents,
    showBurgerMenu,
    aiSearch,
    isTablet,
    fullWidthOnTablet,
    baseContainerWidth
  ]);

  // Enhanced container styles
  const containerStyles = useMemo(() => {
    // Base top position - always use the same value to prevent position shifts
    // This value should NEVER change, regardless of expansion state
    const baseTop = Platform.OS === 'ios' ? 15 : 20;

    // Check if customStyle provides left positioning (to avoid right conflict)
    const hasCustomLeft = customStyle?.left !== undefined;

    return {
      ...componentStyles.container,
      maxWidth: isTablet && fullWidthOnTablet ? undefined : (isTablet ? 400 : componentWidths.container),
      width: Platform.OS === 'web' ? '100%' as const : (isTablet && fullWidthOnTablet ? '100%' as const : componentWidths.container),
      // CRITICAL: Always set base height in layout - this is the layout footprint
      // The animated style will override the visual height when expanded, but layout stays constant
      // This ensures the component doesn't push content down when expanded
      height: responsiveConfig.containerHeight,
      // Ensure the container doesn't affect layout flow when expanded
      // Since it's absolutely positioned, it's already out of flow, but this reinforces it
      // Use appropriate padding for tablet full-width mode to prevent content from touching edges
      paddingHorizontal: isTablet && fullWidthOnTablet ? 20 : responsiveConfig.padding.horizontal,
      // Platform-specific positioning
      ...(Platform.OS === 'web' ? {
        position: (shouldExpand && isExpanded) ? 'fixed' as const : 'relative' as const,
        alignSelf: 'stretch' as const,
        marginHorizontal: 'auto' as const,
        ...(shouldExpand && isExpanded ? {
          top: 20, // Add some space from the top
          left: '58dvw',
          transform: [{ translateX: '-50%' }] as any,
          width: componentWidths.container,
          maxWidth: "75dvw", // Max width when expanded with aiCallback
          zIndex: 1000,
        } : {}),
      } : {
        position: 'absolute' as const,
          // CRITICAL: Always use the same top position - NEVER change it
          // This prevents any position shifts on blur/collapse
          top: baseTop,
          // For tablet full-width mode, position relative to parent (which is full width)
          // For other cases, use padding-based positioning
          left: isTablet && fullWidthOnTablet ? 0 : responsiveConfig.padding.horizontal,
          // Only set right if customStyle doesn't provide left (to avoid conflicts)
          ...(!hasCustomLeft && {
            right: isTablet && fullWidthOnTablet ? 0 : responsiveConfig.padding.horizontal,
          }),
          // Always use high z-index to ensure it overlays content when expanded
          zIndex: 1000,
          // Ensure overflow is visible so expanded content can show
          overflow: 'visible' as const,
          // The container is absolutely positioned, so it's out of the normal flow
          // This means expansion won't push content down - it will overlay instead
      }),
      // Apply customStyle last to ensure it overrides all default styles
      ...customStyle,
    };
  }, [
    customStyle,
    componentWidths.container,
    responsiveConfig.containerHeight,
    responsiveConfig.padding.horizontal,
    shouldExpand,
    isExpanded,
    fullWidthOnTablet
  ]);

  // Enhanced search container styles
  const searchContainerStyles = useMemo(() => {
    const baseMinWidth = searchOnly ? undefined : Math.max(minWidth || responsiveConfig.searchMinWidth, responsiveConfig.searchMinWidth);

    return {
      ...componentStyles.searchContainer,
      backgroundColor: theme ? '#ffffff' : '#1f2937',
      minWidth: baseMinWidth,
      width: Platform.OS === 'web' ? '100%' as const : componentWidths.search,
      height: shouldExpand && isExpanded ? undefined : (Platform.OS === 'android' ? responsiveConfig.containerHeight : responsiveConfig.containerHeight - 8),
      minHeight: shouldExpand && isExpanded ? expandedHeightValue - 100 : (Platform.OS === 'android' ? responsiveConfig.containerHeight : responsiveConfig.containerHeight - 8),
      maxHeight: shouldExpand && isExpanded ? expandedHeightValue - 80 : undefined,
      borderRadius: 20,
      paddingHorizontal: responsiveConfig.spacing.horizontal,
      paddingVertical: shouldExpand && isExpanded ? responsiveConfig.spacing.vertical * 2 : responsiveConfig.spacing.vertical,
      overflow: shouldExpand && isExpanded ? 'visible' as const : 'visible' as const,
      // Enhanced shadows for better depth
      shadowColor: theme ? '#000000' : '#ffffff',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: theme ? 0.1 : 0.05,
      shadowRadius: 12,
      elevation: 8,
      ...(Platform.OS === 'web' && isExpanded && {
        maxWidth: shouldExpand ? 1280 : componentWidths.container, // Max width of 1280px when aiCallback is active
        alignSelf: 'center' as const,
      }),
    };
  }, [
    theme,
    minWidth,
    componentWidths.search,
    responsiveConfig,
    shouldExpand,
    isExpanded,
    expandedHeightValue,
    searchOnly,
    componentWidths.container
  ]);

  // Handle container layout changes
  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setContainerLayout({ width, height });
  }, []);

  // Render other components with improved responsive behavior
  const renderOtherComponents = useCallback(() => {
    if (!otherComponents) return null;

    const maxVisible = responsiveConfig.showAllComponents ?
      otherComponents.length : responsiveConfig.maxVisibleComponents;
    const visibleComponents = otherComponents.slice(0, maxVisible);

    return (
      <View style={[
        componentStyles.otherComponentsContainer,
        {
          maxWidth: componentWidths.otherComponents,
          gap: responsiveConfig.spacing.horizontal / 2,
        }
      ]}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            alignItems: 'center',
            gap: responsiveConfig.spacing.horizontal / 2,
          }}
        >
          {visibleComponents.map((component, index) => {
            if (component.functionName === 'showHelp') return null;

            return (
              <Pressable
                key={`${component.functionName}-${index}`}
                style={({ pressed }) => [
                  {
                    minWidth: responsiveConfig.buttonSize,
                    minHeight: responsiveConfig.buttonSize,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    shadowOffset: { width: 0, height: 4 },
                    shadowRadius: 12,
                    elevation: 4,
                    borderWidth: 1,
                    backgroundColor: theme ? "#ffffff" : "#1e293b",
                    borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
                    shadowColor: theme ? "#000" : "#fff",
                    shadowOpacity: theme ? 0.08 : 0.15,
                    transform: [{ scale: pressed ? 0.95 : 1 }],
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s ease',
                    }),
                  }
                ]}
                onPress={() => {
                  setShowExtendedMenu(false);
                  component.function();
                }}
                accessibilityRole="button"
                accessibilityLabel={component.functionName}
              >
                <View
                  style={[
                    {
                      borderRadius: 14,
                      padding: 8,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }
                  ]}
                >
                  <View style={{
                    backgroundColor: theme ? "#4A90E2" : "#374151",
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {React.isValidElement(component.icon)
                      ? React.cloneElement(component.icon as any, {
                        // Preserve original color if provided, otherwise use white for visibility on colored background
                        color: (component.icon as any).props?.color ?? (theme ? '#ffffff' : '#ffffff'),
                        size: (component.icon as any).props?.size ?? responsiveConfig.iconSize * 0.8,
                      })
                      : component.icon}
                  </View>
                </View>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* Help component */}
        {otherComponents.find(c => c.functionName === 'showHelp') && (
          <View style={[
            componentStyles.helpButton,
            {
              width: responsiveConfig.buttonSize,
              height: responsiveConfig.buttonSize,
              borderRadius: responsiveConfig.searchBorderRadius / 2,
              borderLeftWidth: 1,
              borderColor: theme ? '#e5e7eb' : '#4b5563',
              marginLeft: responsiveConfig.spacing.horizontal / 2,
            }
          ]}>
            <Pressable
              onPress={() => {
                const helpComponent = otherComponents.find(c => c.functionName === 'showHelp');
                if (helpComponent) {
                  setShowExtendedMenu(false);
                  helpComponent.function();
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              accessibilityRole="button"
              accessibilityLabel="Help"
            >
              {(() => {
                const helpComp = otherComponents.find(c => c.functionName === 'showHelp');
                return helpComp && React.isValidElement(helpComp.icon)
                  ? React.cloneElement(helpComp.icon as any, {
                    // Preserve original color if provided, otherwise use theme-based color
                    color: (helpComp.icon as any).props?.color ?? (theme ? '#374151' : '#d1d5db'),
                    size: (helpComp.icon as any).props?.size ?? responsiveConfig.iconSize,
                  })
                  : helpComp?.icon;
              })()}
            </Pressable>
          </View>
        )}
      </View>
    );
  }, [
    otherComponents,
    responsiveConfig,
    componentWidths.otherComponents,
    theme
  ]);

  // Render search section
  const renderSearchSection = useCallback(() => {
    if (otherComponents && !showSearch) return null;
    if (onlyOthers) return null; // Don't render search when onlyOthers is active

    return (
      <View
        style={[
          componentStyles.searchSection,
          {
            flex: 1,
            minHeight: shouldExpand && isExpanded ? expandedHeightValue - 120 : (Platform.OS === 'android' ? responsiveConfig.containerHeight - 8 : responsiveConfig.containerHeight - 16),
          }
        ]}
        pointerEvents="box-none"
      >
        {showSearch && (
          <InteractiveSearch
            aiCallback={wrappedAICallback as any}
            aiSearch={aiSearch}
            placeHolder={placeHolder}
            searchParams={searchParams}
            callback={handleSearchCallback}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            isExpanded={isExpanded}
            multiline={shouldExpand && isExpanded}
            buttonColumnTop={shouldExpand && isExpanded ? (
              responsiveConfig.spacing.vertical +
              (responsiveConfig.buttonSize * 0.7) +
              responsiveConfig.spacing.vertical +
              ((customIcons?.length || 0) * (responsiveConfig.buttonSize * 0.7 + responsiveConfig.spacing.vertical / 2)) +
              responsiveConfig.spacing.vertical
            ) : undefined}
            customIconsCount={customIcons?.length || 0}
            value={searchState}
          />
        )}
      </View>
    );
  }, [
    otherComponents,
    showSearch,
    onlyOthers,
    responsiveConfig.containerHeight,
    aiCallback,
    aiSearch,
    placeHolder,
    searchParams,
    handleSearchCallback,
    shouldExpand,
    isExpanded,
    expandedHeightValue,
    handleInputFocus,
    handleInputBlur,
    responsiveConfig,
    customIcons,
  ]);

  return (
    <>
      {/* Backdrop - only show when expanded and aiCallback exists */}
      {shouldExpand && isExpanded && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            },
            backdropAnimatedStyle,
          ]}
        >
          <Pressable
            style={{ flex: 1 }}
            onPress={handleBackdropPress}
            accessibilityLabel="Close search"
            accessibilityRole="button"
          />
        </Animated.View>
      )}

      {/* Main Container */}
      <Animated.View
        ref={containerRef}
        onLayout={handleContainerLayout}
        style={[
          containerStyles,
          // Always apply animated style when shouldExpand is true
          // It will use base height when collapsed and expanded height when expanded
          shouldExpand ? containerAnimatedStyle : {}
        ]}
        key={`search-responsive-${currentBreakpoint}-${screenDimensions.width}`}
      >
      <View style={[
        componentStyles.content,
        {
          flexDirection: inverse ? 'row-reverse' : 'row',
          gap: responsiveConfig.spacing.horizontal,
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }
      ]}>
        {/* Main search container */}
        <View style={[
          searchContainerStyles,
          showBurgerMenu ? {
            // When burger is shown, constrain width to leave space for burger button
            maxWidth: screenDimensions.width - 120, // Leave space for burger + padding
            flex: 0,
            flexGrow: 1,
            flexShrink: 1,
          } : {
            flex: 1
          }
        ]}>
          <View style={[
            componentStyles.innerContainer,
            {
              // When expanded, change to column layout for right-side buttons
              flexDirection: shouldExpand && isExpanded ? 'row' : 'row',
              gap: aiSearch ? 0 : responsiveConfig.spacing.horizontal / 2,
              minHeight: shouldExpand && isExpanded ? expandedHeightValue - 100 : (Platform.OS === 'android' ? responsiveConfig.containerHeight - 8 : responsiveConfig.containerHeight - 16),
              alignItems: shouldExpand && isExpanded ? 'flex-start' : 'center',
              position: 'relative' as const,
            }
          ]}>
            {/* Other components section - hide when burger menu is shown or when aiCallback is active */}
              {(otherComponents || onlyOthers) && !showBurgerMenu && !shouldExpand && (
                <View style={{ flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                {renderOtherComponents()}
              </View>
            )}

            {/* Search section - don't render when onlyOthers is active */}
            {!onlyOthers && (
                <View style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  // When expanded, add right padding to make room for button column
                  paddingRight: shouldExpand && isExpanded ? 60 : 0,
                }}>
                {renderSearchSection()}
              </View>
            )}

              {/* Right-side button column when expanded */}
              {shouldExpand && isExpanded && (
                <View style={{
                  position: 'absolute' as const,
                  right: responsiveConfig.spacing.horizontal,
                  top: responsiveConfig.spacing.vertical,
                  flexDirection: 'column' as const,
                  alignItems: 'center' as const,
                  gap: responsiveConfig.spacing.vertical,
                  zIndex: 100,
                }}>
                  {/* Close button */}
                  <Pressable
                    onPress={handleCollapse}
                    style={{
                      width: responsiveConfig.buttonSize * 0.7,
                      height: responsiveConfig.buttonSize * 0.7,
                      borderRadius: responsiveConfig.buttonSize * 0.35,
                      backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    accessibilityLabel="Close search"
                    accessibilityRole="button"
                  >
                    <Feather
                      name="x"
                      size={responsiveConfig.iconSize * 0.65}
                      color={theme ? '#333' : '#ccc'}
                    />
                  </Pressable>

                  {/* Custom icons - middle of column */}
                  {customIcons && customIcons.length > 0 && !showBurgerMenu && (
                    <View
                      style={[
                        {
                          flexDirection: 'column' as const,
                          alignItems: 'center' as const,
                          gap: responsiveConfig.spacing.vertical / 2,
                        }
                      ]}
                    >
                      {customIcons.map((icon, index) => (
                        <Pressable
                          key={index}
                          onPress={() => icon.function()}
                          style={{
                            width: responsiveConfig.buttonSize * 0.7,
                            height: responsiveConfig.buttonSize * 0.7,
                            borderRadius: responsiveConfig.buttonSize * 0.35,
                            backgroundColor: theme ? '#f3f4f6' : '#374151',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {React.isValidElement(icon.icon)
                            ? React.cloneElement(icon.icon as any, {
                              // Preserve original color if provided, otherwise use theme-based color
                              color: (icon.icon as any).props?.color ?? (theme ? '#374151' : '#d1d5db'),
                              size: (icon.icon as any).props?.size ?? (responsiveConfig.iconSize * 0.7),
                            })
                            : icon.icon}
                        </Pressable>
                      ))}
                    </View>
                  )}
                </View>
              )}

              {/* Custom icons - only show when NOT expanded and NOT using burger menu */}
              {customIcons && customIcons.length > 0 && !showBurgerMenu && !(shouldExpand && isExpanded) && (
              <View
                style={[
                  componentStyles.customIconsContainer,
                  {
                    width: componentWidths.icons,
                    height: responsiveConfig.containerHeight - 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: aiSearch ? -16 : 0,
                    flexShrink: 0, // Prevent shrinking
                    overflow: 'visible', // Allow icons to be visible
                  }
                ]}
              >
                <GenerateArray
                  customIconStyle={customIconStyle}
                  disableBurger={disableBurger}
                  customIcons={customIcons}
                  setShowExtendedMenu={setShowExtendedMenu}
                  width={componentWidths.icons}
                  extraComponents={extraComponents}
                  otherComponents={otherComponents}
                />
              </View>
            )}

              {/* Extra components - hide when burger menu is shown or expanded */}
              {extraComponents && !showBurgerMenu && !(shouldExpand && isExpanded) && (
              <View style={[
                componentStyles.extraComponentsContainer,
                {
                  height: responsiveConfig.containerHeight - 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              ]}>
                {extraComponents}
              </View>
            )}
          </View>
        </View>

        {/* Burger menu button - positioned after search bar on mobile */}
        {customIcons && customIcons.length > 0 && showBurgerMenu && (
          <View
            style={[
              componentStyles.burgerMenuContainer,
              {
                height: responsiveConfig.containerHeight - 8,
                justifyContent: 'center',
                alignItems: 'center',
              }
            ]}
          >
            <GenerateArray
              customIconStyle={customIconStyle}
              disableBurger={false}
              customIcons={customIcons}
              setShowExtendedMenu={setShowExtendedMenu}
              width={100}
              extraComponents={extraComponents}
              otherComponents={otherComponents}
            />
          </View>
        )}

        {/* Action button */}
        {actionButton && (
          <Pressable
            ref={buttonRef}
            disabled={!searchState}
            onPress={() => action?.()}
            style={[
              componentStyles.actionButton,
              {
                opacity: searchState ? 1 : 0.5,
                width: responsiveConfig.buttonSize,
                height: responsiveConfig.buttonSize,
                borderRadius: responsiveConfig.searchBorderRadius / 2,
                backgroundColor: theme ? '#7392d1ff' : '#374151',
                marginLeft: responsiveConfig.spacing.horizontal,
              }
            ]}
            accessibilityRole="button"
            accessibilityLabel="Send"
          >
            <Feather
              name="send"
              size={responsiveConfig.iconSize}
              color={searchState ? "#3b82f6" : '#9ca3af'}
            />
          </Pressable>
        )}
      </View>

      {/* Loading indicator */}
      {fetchingState && (
        <Animatable.View
          animation="fadeIn"
          style={[
            componentStyles.loadingContainer,
            {
              right: responsiveConfig.spacing.horizontal,
              top: (responsiveConfig.containerHeight - 20) / 2,
            }
          ]}
        >
          <LoadingIndicator
            type="indeterminate"
            color="#3b82f6"
          />
        </Animatable.View>
      )}

        {/* Close button is now in the right column when expanded */}
      </Animated.View>

      {/* AI Credits Modal */}
      <AICreditsModal
        open={showAICreditsModal}
        onClose={() => setShowAICreditsModal(false)}
        onPurchase={(packageId) => {
          // Close modal first
          setShowAICreditsModal(false);

          // Navigate to upgrade screen for purchasing credits
          // The modal shows credit packages, but actual purchase happens on upgrade screen
          try {
            (navigation as any).navigate('Settings', {
              screen: 'Upgrade',
              params: {
                initialTab: 'credits' // If your upgrade screen supports tab navigation
              }
            });
          } catch (error) {
            console.error('Failed to navigate to upgrade screen:', error);
          }
        }}
      />
    </>
  );
};

// Enhanced responsive styles
const componentStyles = StyleSheet.create({
  container: {
    zIndex: 1000,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // Ensure overflow is visible so expanded content can overlay
    overflow: 'visible' as const,
  },
  content: {
    width: '100%',
    alignItems: 'center',
  },
  searchContainer: {
    overflow: 'visible',
    justifyContent: 'center',
    top: Platform.OS === 'android' ? 0 : -3,
    alignItems: 'stretch',
    // Modern glass morphism effect
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }),
  },
  innerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 40,
    alignContent: 'center',
  },
  otherComponentsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexShrink: 0,
    alignContent: 'center',
  },
  componentButton: {
    alignItems: 'center',
    justifyContent: 'center',
    // Modern hover effects for web
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
    }),
  },
  helpButton: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  searchSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    width: '100%',
  },
  customIconsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignContent: 'center',
  },
  extraComponentsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    alignContent: 'center',
  },
  burgerMenuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 48,
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Modern hover effects for web
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
    }),
  },
  loadingContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
});

// Memoize the component to prevent unnecessary re-renders
export default memo(SearchComponent, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.searchState === nextProps.searchState &&
    prevProps.fetchingState === nextProps.fetchingState &&
    prevProps.aiSearch === nextProps.aiSearch &&
    prevProps.showSearch === nextProps.showSearch &&
    prevProps.customIcons === nextProps.customIcons &&
    prevProps.extraComponents === nextProps.extraComponents &&
    prevProps.theme === nextProps.theme
  );
});